<template>
  <section id="formulario" class="bg-transparent p-6 plex-regular flex justify-center items-center"
    style="font-family: 'Poppins', sans-serif;">

    <!-- STEP 1: DNI INPUT -->
    <div v-if="!showFullForm" class="w-full max-w-md bg-transparent  p-8 ">
      <div class="w-full pb-6 text-center">
        <h1 class="plex-semibold text-3xl text-gray-800 tracking-tight" style="font-family: 'Poppins', sans-serif;">
          OBTENER MI PASE</h1>

      </div>

      <div class="space-y-4">
        <div>
          <label class="block mb-2 text-sm font-semibold text-gray-700" style="font-family: 'Poppins', sans-serif;">DNI
            / Cédula de Identidad / Pasaporte</label>
          <InputText v-model="formData.dni" type="text" placeholder="DNI / Cédula de Identidad / Pasaporte"
            class="input-base" :class="{ 'input-error': dniError }" @keyup.enter="checkDniAndContinue"
            style="font-family: 'Poppins', sans-serif;" />
          <span v-if="dniError" class="text-xs text-red-500 mt-1 block" style="font-family: 'Poppins', sans-serif;">
            {{ dniError }}
          </span>
        </div>

        <button type="button" @click="checkDniAndContinue"
          class="w-full mt-2 px-4 py-3 bg-[#ec1c24] border border-[#ec1c24] text-white hover:bg-white hover:text-[#ec1c24] font-semibold  transition duration-200 flex justify-center items-center"
          :disabled="isCheckingDni" style="font-family: 'Poppins', sans-serif;">
          <i v-show="isCheckingDni" class="pi pi-spin pi-spinner mr-2"></i>
          CONTINUAR
        </button>
      </div>
    </div>

    <!-- STEP 2: FULL FORM -->
    <Form v-else @submit="onSubmit" :validation-schema="schema" class="w-full max-w-7xl"
      @invalid-submit="onInvalidSubmit">

      <div class="bg-transparent  p-8 ">
        <div class="w-full pb-6 text-center">
          <h1 class="plex-semibold text-3xl text-gray-800" style="font-family: 'Poppins', sans-serif;">COMPLETÁ ESTE
            FORMULARIO PARA OBTENER TU PASE A LA EXPO</h1>

        </div>

        <!-- DNI, NOMBRE, APELLIDO -->
        <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
          <div class="w-full px-2 md:w-4/12">
            <label class="block mb-1 font-semibold text-sm text-gray-700"
              style="font-family: 'Poppins', sans-serif;">DNI / Pasaporte*</label>
            <Field name="dni" v-slot="{ field, errors }" v-model="formData.dni">
              <InputText v-bind="field" type="text" placeholder="DNI / Pasaporte" class="input-base" readonly
                :class="{ 'bg-gray-100': true, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }"
                style="font-family: 'Poppins', sans-serif;" />
              <span class="text-sm text-red-500 p-error" style="font-family: 'Poppins', sans-serif;">
                <ErrorMessage name="dni" />
              </span>
            </Field>
          </div>
          <div class="w-full px-2 md:w-4/12">
            <label class="block mb-1 font-semibold text-sm text-gray-700"
              style="font-family: 'Poppins', sans-serif;">Nombre*</label>
            <Field name="nombre" v-slot="{ field, errors }" v-model="formData.nombre">
              <InputText v-bind="field" type="text" placeholder="Nombre" class="input-base"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }"
                style="font-family: 'Poppins', sans-serif;" />
              <span class="text-sm text-red-500 p-error" style="font-family: 'Poppins', sans-serif;">
                <ErrorMessage name="nombre" />
              </span>
            </Field>
          </div>
          <div class="w-full px-2 md:w-4/12">
            <label class="block mb-1 font-semibold text-sm text-gray-700"
              style="font-family: 'Poppins', sans-serif;">Apellido*</label>
            <Field name="apellido" v-slot="{ field, errors }" v-model="formData.apellido">
              <InputText v-bind="field" type="text" placeholder="Apellido" class="input-base"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }"
                style="font-family: 'Poppins', sans-serif;" />
              <span class="text-sm text-red-500 p-error" style="font-family: 'Poppins', sans-serif;">
                <ErrorMessage name="apellido" />
              </span>
            </Field>
          </div>
        </div>

        <!-- EMAIL -->
        <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0 mt-4">
          <div class="w-full px-2">
            <label class="block mb-1 font-semibold text-sm text-gray-700"
              style="font-family: 'Poppins', sans-serif;">Email*</label>
            <Field name="mail" v-slot="{ field, errors }" v-model="formData.mail">
              <InputText v-bind="field" type="email" placeholder="Email" class="input-base"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }"
                style="font-family: 'Poppins', sans-serif;" />
              <span class="text-sm text-red-500 p-error" style="font-family: 'Poppins', sans-serif;">
                <ErrorMessage name="mail" />
              </span>
            </Field>
          </div>
        </div>

        <!-- CELULAR (Phone Country, Code Area, Phone) -->
        <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0 mt-4">
          <div class="w-full px-2">
            <div class="flex flex-wrap -mx-2">
              <div class="w-full md:w-4/12 px-2">
                <label class="block mb-1 font-semibold text-sm text-gray-700"
                  style="font-family: 'Poppins', sans-serif;">País Celular*</label>
                <Select v-model="selectedCountryPhone" filter :options="paises" optionLabel="descripcion"
                  placeholder="País" class="input-base input-success" style="font-family: 'Poppins', sans-serif;">
                  <template #value="slotProps">
                    <div v-if="slotProps.value" class="flex items-center">
                      <img v-if="slotProps.value.isocode" :src="getFlagUrl(slotProps.value.isocode)"
                        class="mr-2 border border-gray-200" style="width: 20px; height: auto;" />
                      <div>{{ slotProps.value.descripcion }}</div>
                    </div>
                    <span v-else>
                      {{ slotProps.placeholder }}
                    </span>
                  </template>
                  <template #option="slotProps">
                    <div class="flex items-center">
                      <img v-if="slotProps.option.isocode" :src="getFlagUrl(slotProps.option.isocode)"
                        class="mr-2 border border-gray-200" style="width: 20px; height: auto;" />
                      <div>{{ slotProps.option.descripcion }}</div>
                    </div>
                  </template>
                </Select>
              </div>
              <div class="w-full md:w-2/12 px-2">
                <label class="block mb-1 font-semibold text-sm text-gray-700"
                  style="font-family: 'Poppins', sans-serif;">Cód.País*</label>
                <InputText type="text" v-if="selectedCountryPhone !== null" v-model="selectedCountryPhone.id"
                  class="input-base input-success" readonly style="font-family: 'Poppins', sans-serif;" />
              </div>
              <div class="w-full md:w-6/12 px-2">
                <label class="block mb-1 font-semibold text-sm text-gray-700"
                  style="font-family: 'Poppins', sans-serif;">Tel. Celular
                  (con código de área)*</label>
                <Field name="telefonoCelular" v-slot="{ field, errors }" v-model="formData.telefonoCelular">
                  <InputText v-bind="field" type="text" placeholder="Número (ej: 1112345678)" class="input-base"
                    :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }"
                    style="font-family: 'Poppins', sans-serif;" />
                  <span class="text-sm text-red-500 p-error" style="font-family: 'Poppins', sans-serif;">
                    <ErrorMessage name="telefonoCelular" />
                  </span>
                </Field>
              </div>
            </div>
          </div>
        </div>

        <!-- PAIS Y CIUDAD -->
        <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0 mt-4">
          <div class="w-full px-2 md:w-1/2">
            <label class="block mb-1 font-semibold text-sm text-gray-700"
              style="font-family: 'Poppins', sans-serif;">País*</label>
            <Field name="pais" v-slot="{ handleChange, handleBlur, value, errors }" v-model="formData.pais">
              <Select filter @blur="handleBlur" @update:model-value="handleChange" :model-value="value"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && value !== undefined, 'input-error': errors.length > 0 }"
                placeholder="Seleccione su país" class="input-base" :options="paises" optionLabel="descripcion"
                style="font-family: 'Poppins', sans-serif;">
                <template #value="slotProps">
                  <div v-if="slotProps.value" class="flex items-center">
                    <img v-if="slotProps.value.isocode" :src="getFlagUrl(slotProps.value.isocode)"
                      class="mr-2 border border-gray-200" style="width: 20px; height: auto;" />
                    <div>{{ slotProps.value.descripcion }}</div>
                  </div>
                  <span v-else>
                    {{ slotProps.placeholder }}
                  </span>
                </template>
                <template #option="slotProps">
                  <div class="flex items-center">
                    <img v-if="slotProps.option.isocode" :src="getFlagUrl(slotProps.option.isocode)"
                      class="mr-2 border border-gray-200" style="width: 20px; height: auto;" />
                    <div>{{ slotProps.option.descripcion }}</div>
                  </div>
                </template>
              </Select>
              <span class="text-sm text-red-500 p-error" style="font-family: 'Poppins', sans-serif;">
                <ErrorMessage name="pais" />
              </span>
            </Field>
          </div>
          <div class="w-full px-2 md:w-1/2">
            <label class="block mb-1 font-semibold text-sm text-gray-700"
              style="font-family: 'Poppins', sans-serif;">Ciudad*</label>
            <Field name="ciudad" v-slot="{ field, errors }" v-model="formData.ciudad">
              <InputText v-bind="field" type="text" placeholder="Ingrese su ciudad" class="input-base"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }"
                style="font-family: 'Poppins', sans-serif;" />
              <span class="text-sm text-red-500 p-error" style="font-family: 'Poppins', sans-serif;">
                <ErrorMessage name="ciudad" />
              </span>
            </Field>
          </div>
        </div>

        <!-- ¿Qué tipo de visitante sos? -->
        <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0 mt-4">
          <div class="w-full px-2">
            <label class="block mb-1 font-semibold text-sm text-gray-700"
              style="font-family: 'Poppins', sans-serif;">¿Qué tipo
              de visitante sos?*</label>
            <Field name="tipoVisitante" v-slot="{ handleChange, handleBlur, value, errors }"
              v-model="formData.tipoVisitante">
              <Select @blur="handleBlur" @update:model-value="handleChange" :model-value="value"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && value !== undefined, 'input-error': errors.length > 0 }"
                placeholder="Seleccione una opción" class="input-base" :options="tipoVisitantes"
                optionLabel="descripcion" style="font-family: 'Poppins', sans-serif;" />
              <span class="text-sm text-red-500 p-error" style="font-family: 'Poppins', sans-serif;">
                <ErrorMessage name="tipoVisitante" />
              </span>
            </Field>
          </div>
        </div>

        <div v-if="formData.tipoVisitante?.id === 'Profesional'">

          <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0 mt-4">
            <div class="w-full px-2">
              <label class="block mb-1 font-semibold text-sm text-gray-700"
                style="font-family: 'Poppins', sans-serif;">¿Cuál
                describe mejor tu actividad?*</label>
              <Field name="actividad" v-slot="{ handleChange, handleBlur, value, errors }" v-model="formData.actividad">
                <Select @blur="handleBlur" @update:model-value="handleChange" :model-value="value"
                  :class="{ 'bg-white': errors.length === 0, 'input-success': errors.length === 0 && value !== undefined, 'input-error': errors.length > 0 }"
                  placeholder="Seleccione su actividad" class="input-base" :options="actividades"
                  optionLabel="descripcion" style="font-family: 'Poppins', sans-serif;" />
                <span class="text-sm text-red-500 p-error" style="font-family: 'Poppins', sans-serif;">
                  <ErrorMessage name="actividad" />
                </span>
              </Field>
            </div>
          </div>
          <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0 mt-4">
            <div class="w-full px-2">
              <label class="block mb-1 font-semibold text-sm text-gray-700"
                style="font-family: 'Poppins', sans-serif;">Ingresá
                tu marca / empresa*</label>
              <Field name="marcaEmpresa" v-slot="{ field, errors }" v-model="formData.marcaEmpresa">
                <InputText v-bind="field" type="text" placeholder="Marca / Empresa" class="input-base"
                  :class="{ 'bg-white': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }"
                  style="font-family: 'Poppins', sans-serif;" />
                <span class="text-sm text-red-500 p-error" style="font-family: 'Poppins', sans-serif;">
                  <ErrorMessage name="marcaEmpresa" />
                </span>
              </Field>
            </div>
          </div>
        </div>

        <div class="flex flex-wrap py-2 mt-6 justify-between items-center">
          <button type="button" @click="showFullForm = false"
            class="px-4 py-2 border border-gray-300 hover:border-gray-800 text-gray-600 hover:text-gray-800 rounded-lg transition duration-200 flex items-center"
            style="font-family: 'Poppins', sans-serif;">
            <i class="pi pi-arrow-left mr-2"></i> VOLVER
          </button>

          <button type="submit"
            class="px-6 py-3 bg-[#ec1c24] border border-[#ec1c24] text-white hover:bg-white hover:text-[#ec1c24] font-semibold rounded-lg transition duration-200 flex items-center shadow-md"
            :disabled="isSubmitting" style="font-family: 'Poppins', sans-serif;">
            <i v-show="isSubmitting" class="pi pi-spin pi-spinner mr-2"></i>
            <i v-show="!isSubmitting" class="pi pi-check mr-2"></i> ENVIAR MIS DATOS
          </button>
        </div>
      </div>
    </Form>
  </section>
</template>

<script lang="ts">
import * as yup from 'yup';

import { defineComponent } from 'vue';
import { Field, Form, ErrorMessage } from 'vee-validate';
import Swal from 'sweetalert2'

import InputText from 'primevue/inputtext';
import Select from 'primevue/select';

import jsonCountries from '@/data/countries.json';
import jsonTipoVisitante from '@/data/tipoVisitante.json';
import jsonActividades from '@/data/actividades.json';

import suiteService, { Acreditado, TipoAcreditado, EstadoAcreditado, OrigenAcreditado, StringIdLabelEntity } from 'qreventos-suite/services';
import suiteConfig from '@/services/suite.config';
import { SAVE, ORIGEN_ACREDITADO, VERIFICADO, UPDATE } from "qreventos-suite/constantes";

interface Pais extends StringIdLabelEntity {
  isocode?: string;
}

interface TipoVisitante {
  id: string;
  descripcion: string;
}
interface Actividad {
  id: string;
  descripcion: string;
}

export default defineComponent({
  name: 'FormSection',
  components: {
    InputText, Select,
    Field, ErrorMessage, Form
  },
  data() {
    return {
      paises: jsonCountries as Pais[],
      tipoVisitantes: jsonTipoVisitante as TipoVisitante[],
      actividades: jsonActividades as Actividad[],
      selectedCountryPhone: { "id": "+54", "descripcion": "Argentina", "isocode": "AR" } as Pais,
      isSubmitting: false,
      isCheckingDni: false,
      showFullForm: false,
      dniError: '',
      existingAcreditado: undefined as unknown as Acreditado,
      formData: {
        dni: '',
        nombre: '',
        apellido: '',
        mail: '',
        telefonoCelular: '',
        pais: { "id": "+54", "descripcion": "Argentina", "isocode": "AR" } as Pais,
        ciudad: '',
        tipoVisitante: undefined as unknown as TipoVisitante,
        actividad: undefined as unknown as Actividad,
        marcaEmpresa: '',
      }
    };
  },
  computed: {
    schema() {
      return yup.object({
        dni: yup.string().required('Ingrese el DNI').min(8, 'Al menos 8 números o caracteres'),
        nombre: yup.string().required('Ingrese el nombre').min(3, 'Al menos 3 letras'),
        apellido: yup.string().required('Ingrese el apellido').min(3, 'Al menos 3 letras'),
        mail: yup.string().required('Ingrese el correo').email('Correo inválido'),
        telefonoCelular: yup.string().required('Ingrese el celular').matches(/^\d+$/, 'Solo se permiten números').min(8, 'Deben ser un mínimo de 8 números').max(20, 'Deben ser menos de 20 números'),
        pais: yup.mixed().required('Seleccione su país'),
        ciudad: yup.string().required('Ingrese su ciudad').min(2, 'Al menos 2 letras'),
        tipoVisitante: yup.mixed().required('Seleccione qué tipo de visitante es'),
        actividad: yup.mixed().test('validation-actividad', 'Seleccione su actividad', function (value) {
          const tipoVisitante = this.parent.tipoVisitante;
          if (tipoVisitante?.id === 'Profesional' && !value) {
            return false;
          }
          return true;
        }),
        marcaEmpresa: yup.string().test('validation-marca-empresa', 'Ingrese su marca / empresa', function (value) {
          const tipoVisitante = this.parent.tipoVisitante;
          if (tipoVisitante?.id === 'Profesional' && !value) {
            return false;
          }
          return true;
        }),
      });
    }
  },
  watch: {
    'formData.tipoVisitante'(newValue) {
      if (newValue?.id !== 'Profesional') {
        this.formData.actividad = undefined as unknown as Actividad;
        this.formData.marcaEmpresa = '';
      }
    }
  },
  methods: {
    onInvalidSubmit(values: any) {
      const errores: number = Object.values(values?.errors || {}).flat().length;
      Swal.fire({ title: "Error", html: "Revise el formulario<br>Hay " + errores + " error/es", icon: "error" });
    },
    getFlagUrl(isocode: string) {
      if (!isocode) return '';
      try {
        return require(`@/assets/flags/${isocode.toLowerCase()}.svg`);
      } catch (e) {
        return '';
      }
    },
    async checkDniAndContinue() {
      this.dniError = '';
      const inputDni = (this.formData.dni || '').trim();
      if (!inputDni) {
        this.dniError = 'Ingrese el DNI / Pasaporte';
        return;
      }
      if (inputDni.length < 8) {
        this.dniError = 'Al menos 8 números o caracteres';
        return;
      }

      this.isCheckingDni = true;
      try {
        let acreditadoExistente: Acreditado[] = await suiteService.findAcreditadosByFilters(suiteConfig.evento, "dni=" + encodeURIComponent(inputDni));
        if (acreditadoExistente && acreditadoExistente.length > 0) {
          const existing = acreditadoExistente[0];
          this.existingAcreditado = existing;
          if (existing.estadoAcreditado.id == VERIFICADO) {
            this.isCheckingDni = false;
            const hash: String = existing.hash;
            this.$router.push({ name: 'Finalizado', params: { hash: hash as string } });
            return;
          }


          this.formData.nombre = existing.nombre;
          this.formData.apellido = existing.apellido;
          this.formData.mail = existing.mail;



          let countryDesc = existing.dato1 || existing.dato2 || '';
          let foundCountry = this.paises.find(p => p.descripcion.toLowerCase() === countryDesc.toLowerCase());
          if (foundCountry) {
            this.formData.pais = foundCountry;
          } else {
            this.formData.pais = { "id": "+54", "descripcion": "Argentina", "isocode": "AR" } as Pais;
          }

          this.formData.ciudad = existing.dato2;

          // Cargar código de país del teléfono
          let countryPhoneId = existing.dato5 || '';
          let foundPhoneCountry = this.paises.find(p => p.id === countryPhoneId);
          if (foundPhoneCountry) {
            this.selectedCountryPhone = foundPhoneCountry;
          } else {
            if (foundCountry) {
              this.selectedCountryPhone = foundCountry;
            } else {
              this.selectedCountryPhone = { "id": "+54", "descripcion": "Argentina", "isocode": "AR" } as Pais;
            }
          }

          // Cargar número de teléfono (removiendo el prefijo de país si coincide)
          if (existing.telefono) {
            let phoneStr = existing.telefono;
            if (phoneStr.startsWith(this.selectedCountryPhone.id)) {
              this.formData.telefonoCelular = phoneStr.slice(this.selectedCountryPhone.id.length);
            } else {
              this.formData.telefonoCelular = phoneStr;
            }
          } else {
            this.formData.telefonoCelular = '';
          }
        } else {
          this.existingAcreditado = undefined as unknown as Acreditado;
          this.formData.nombre = '';
          this.formData.apellido = '';
          this.formData.mail = '';
          this.formData.telefonoCelular = '';
          this.formData.pais = { "id": "+54", "descripcion": "Argentina", "isocode": "AR" } as Pais;
          this.formData.ciudad = '';
          this.formData.tipoVisitante = undefined as unknown as TipoVisitante;
          this.formData.actividad = undefined as unknown as Actividad;
          this.formData.marcaEmpresa = '';
        }

        this.showFullForm = true;
      } catch (error) {
        Swal.fire({ title: "Error", text: "Error al consultar DNI: " + error, icon: "error" });
      } finally {
        this.isCheckingDni = false;
      }
    },
    async onSubmit() {
      this.isSubmitting = true;

      try {
        let acreditado: Acreditado = new Object() as Acreditado;
        if (this.existingAcreditado !== undefined) {
          acreditado = this.existingAcreditado;
        }

        acreditado.tipoAcreditado = { "id": 2503 } as TipoAcreditado;
        acreditado.estadoAcreditado = { "id": VERIFICADO } as EstadoAcreditado;
        acreditado.dni = this.formData.dni;
        acreditado.nombre = this.formData.nombre;
        acreditado.apellido = this.formData.apellido;
        acreditado.telefono = this.selectedCountryPhone.id + this.formData.telefonoCelular;
        acreditado.mail = this.formData.mail;

        acreditado.origenAcreditado = { "id": ORIGEN_ACREDITADO } as OrigenAcreditado;
        if (acreditado.id > 0) {
          acreditado.modificado = UPDATE;
        } else {
          acreditado.modificado = SAVE;
        }
        acreditado.cantidad = 1;
        acreditado.notificar = true;


        acreditado.dato1 = this.formData.pais?.descripcion;
        acreditado.dato2 = this.formData.ciudad;
        acreditado.dato3 = this.formData.tipoVisitante?.descripcion || '';

        if (this.formData.tipoVisitante?.id === 'Profesional') {
          acreditado.dato4 = this.formData.actividad?.descripcion || '';
          acreditado.empresa = this.formData.marcaEmpresa;
        } else {
          acreditado.dato4 = '';
          acreditado.empresa = '';
        }

        acreditado.dato5 = this.selectedCountryPhone.id;
        await suiteService.saveOrUpdate(suiteConfig.evento, acreditado);
        let credencial: Acreditado = await suiteService.findAcreditadoByFilters(suiteConfig.evento, "dni=" + acreditado.dni) as Acreditado;
        const hash: String = credencial.hash;
        this.$router.push({ name: 'Finalizado', params: { hash: hash as string } });
      } catch (error) {
        Swal.fire({ title: "Error", text: error + '', icon: "error" });
      } finally {
        this.isSubmitting = false;
      }
    }
  },
  async mounted() {

  }
});
</script>
